import "./Auth.css";

// ─── Avatar config ─────────────────────────────────────────────────────────────

interface AvatarConfig {
  id: string;
  label: string;
  bg: string;
  skin: string;
  hair: string;
  hairStyle: "short" | "long" | "bun";
  gakti: string;
  band1: string;
  band2: string;
  feminine: boolean;
}

const AVATARS: AvatarConfig[] = [
  { id: "avatar_1", label: "Ájdna",  bg: "#1e3a6c", skin: "#f5c5a3", hair: "#2a1409", hairStyle: "bun",   gakti: "#2a5cad", band1: "#c8971a", band2: "#e8c830", feminine: true  },
  { id: "avatar_2", label: "Máret",  bg: "#6c1e1e", skin: "#d4956a", hair: "#1a1209", hairStyle: "long",  gakti: "#9c2a2a", band1: "#f0f0e8", band2: "#c8c0a8", feminine: true  },
  { id: "avatar_3", label: "Ántte",  bg: "#1e4c1e", skin: "#c8a07a", hair: "#2a1a0a", hairStyle: "short", gakti: "#2a6c2a", band1: "#c1440e", band2: "#e8b830", feminine: false },
  { id: "avatar_4", label: "Elle",   bg: "#3c1e6c", skin: "#8c5a2a", hair: "#1a0a0a", hairStyle: "bun",   gakti: "#5a2a9c", band1: "#c8971a", band2: "#f0e8c0", feminine: true  },
  { id: "avatar_5", label: "Isak",   bg: "#1e4c4c", skin: "#e8c49a", hair: "#6b3a2a", hairStyle: "short", gakti: "#2a7c7c", band1: "#e8c41a", band2: "#1a5c8c", feminine: false },
  { id: "avatar_6", label: "Risten", bg: "#5c3010", skin: "#9c6840", hair: "#0a0a14", hairStyle: "long",  gakti: "#c15a1a", band1: "#1a5c8c", band2: "#4a9cdc", feminine: true  },
  { id: "avatar_7", label: "Johan",  bg: "#1a1a3c", skin: "#d4a07a", hair: "#7c7c7c", hairStyle: "short", gakti: "#1a1a6c", band1: "#c1440e", band2: "#e8c830", feminine: false },
  { id: "avatar_8", label: "Biret",  bg: "#4c1a2a", skin: "#c8906a", hair: "#3d1a0a", hairStyle: "bun",   gakti: "#7c1a3a", band1: "#f0f0e8", band2: "#c8c0a8", feminine: true  },
];

// ─── SVG figure ────────────────────────────────────────────────────────────────

function AvatarSVG({ cfg }: { cfg: AvatarConfig }) {
  const { skin, hair, hairStyle, gakti, band1, band2, bg, feminine } = cfg;

  return (
    <svg
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="avatar-option__svg"
      aria-hidden="true"
    >
      {/* Background */}
      <circle cx="50" cy="50" r="50" fill={bg} />

      {/* ─ Hair back layer ─ */}
      {hairStyle === "long" && (
        <>
          <rect x="27" y="28" width="9" height="26" rx="4.5" fill={hair} />
          <rect x="64" y="28" width="9" height="26" rx="4.5" fill={hair} />
        </>
      )}
      {hairStyle === "bun" && (
        <>
          <circle cx="50" cy="13" r="9" fill={hair} />
          <circle cx="50" cy="13" r="5.5" fill={hair} opacity="0.55" />
        </>
      )}

      {/* ─ Head ─ */}
      <circle cx="50" cy="35" r="17" fill={skin} />

      {/* ─ Hair top/front ─ */}
      <ellipse cx="50" cy="21" rx="15.5" ry="9" fill={hair} />

      {/* ─ Eyebrows ─ */}
      <path d="M 42,29 Q 45.5,27 49,29" stroke={hair} strokeWidth="1.3" strokeLinecap="round" fill="none" />
      <path d="M 51,29 Q 54.5,27 58,29" stroke={hair} strokeWidth="1.3" strokeLinecap="round" fill="none" />

      {/* ─ Eyes ─ */}
      <circle cx="44" cy="34" r="2.6" fill="#1a1209" />
      <circle cx="56" cy="34" r="2.6" fill="#1a1209" />
      {/* Eye shine */}
      <circle cx="45.2" cy="33" r="1" fill="white" />
      <circle cx="57.2" cy="33" r="1" fill="white" />
      {/* Eyelashes (feminine) */}
      {feminine && (
        <>
          <line x1="41.5" y1="32" x2="39.5" y2="30" stroke={hair} strokeWidth="1" strokeLinecap="round" />
          <line x1="58.5" y1="32" x2="60.5" y2="30" stroke={hair} strokeWidth="1" strokeLinecap="round" />
        </>
      )}

      {/* ─ Nose ─ */}
      <circle cx="48.5" cy="39.5" r="0.9" fill="#b07848" />
      <circle cx="51.5" cy="39.5" r="0.9" fill="#b07848" />

      {/* ─ Mouth / smile ─ */}
      <path d="M 44,44 Q 50,49 56,44" stroke="#1a1209" strokeWidth="1.5" strokeLinecap="round" fill="none" />

      {/* ─ Cheek blush (feminine) ─ */}
      {feminine && (
        <>
          <ellipse cx="40" cy="39" rx="4.5" ry="2.5" fill="#e88080" opacity="0.35" />
          <ellipse cx="60" cy="39" rx="4.5" ry="2.5" fill="#e88080" opacity="0.35" />
        </>
      )}

      {/* ─ Neck ─ */}
      <rect x="44" y="51" width="12" height="8" fill={skin} />

      {/* ─ Gákti body (trapezoid, clips at circle boundary) ─ */}
      <path d="M 16,57 L 84,57 L 96,102 L 4,102 Z" fill={gakti} />

      {/* ─ Arms ─ */}
      <rect x="4"  y="55" width="14" height="30" rx="7" fill={gakti} />
      <rect x="82" y="55" width="14" height="30" rx="7" fill={gakti} />

      {/* ─ Collar / front band ─ */}
      <path d="M 43,53 L 57,53 L 55,65 L 45,65 Z" fill={band1} />
      <line x1="50" y1="53" x2="50" y2="65" stroke={band2} strokeWidth="1" opacity="0.6" />

      {/* ─ Belt / sash ─ */}
      <rect x="16" y="70" width="68" height="7" rx="3" fill={band2} />
      {/* Belt buckle accent */}
      <rect x="46" y="71" width="8" height="5" rx="1.5" fill={band1} />

      {/* ─ Hem band ─ */}
      <rect x="6"  y="90" width="88" height="7" rx="2" fill={band1} />

      {/* ─ Cuff bands ─ */}
      <rect x="4"  y="78" width="14" height="6" rx="3" fill={band1} />
      <rect x="82" y="78" width="14" height="6" rx="3" fill={band1} />

      {/* ─ Shoulder seam lines ─ */}
      <path d="M 16,57 L 8,55" stroke={band1} strokeWidth="1" strokeLinecap="round" opacity="0.5" />
      <path d="M 84,57 L 92,55" stroke={band1} strokeWidth="1" strokeLinecap="round" opacity="0.5" />

      {/* ─ Circular mask so clipping looks clean ─ */}
      <circle cx="50" cy="50" r="49.5" fill="none" stroke={bg} strokeWidth="1" />
    </svg>
  );
}

// ─── Picker component ──────────────────────────────────────────────────────────

interface Props {
  value: string;
  onChange: (avatarId: string) => void;
}

export function AvatarPicker({ value, onChange }: Props) {
  return (
    <div>
      <p className="auth-section-label">Velg avatar</p>
      <div className="avatar-grid">
        {AVATARS.map((cfg) => (
          <button
            key={cfg.id}
            type="button"
            className={`avatar-option${value === cfg.id ? " avatar-option--selected" : ""}`}
            onClick={() => onChange(cfg.id)}
            aria-label={cfg.label}
            aria-pressed={value === cfg.id}
            title={cfg.label}
          >
            <AvatarSVG cfg={cfg} />
            {value === cfg.id && (
              <span className="avatar-option__check" aria-hidden="true">✓</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Standalone avatar display (used in HUD, profile, etc.) */
export function Avatar({ avatarId, size = 40 }: { avatarId: string; size?: number }) {
  const cfg = AVATARS.find((a) => a.id === avatarId) ?? AVATARS[0];
  return (
    <span
      style={{ width: size, height: size, display: "inline-block", borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}
      aria-label={cfg.label}
    >
      <AvatarSVG cfg={cfg} />
    </span>
  );
}

export { AVATARS };
